import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Check, XCircle, Download, FileText, AlertCircle, X } from 'lucide-react';
import { sickLeaveRequestApi } from '../services/api';
import { SickLeaveRequest } from '../types';
import { useLocationContext } from '../components/Layout';
import { formatCompanyDate } from '../utils/companyDateFormat';
import { triggerBlobDownload } from '../utils/certificateFiles';

const SickLeaveAdmin: React.FC = () => {
  const { selectedLocationId } = useLocationContext();
  const [requests, setRequests] = useState<SickLeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('Submitted');
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<SickLeaveRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await sickLeaveRequestApi.getAll(undefined, statusFilter || undefined);
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => requests, [requests]);

  const approve = async (id: number) => {
    try {
      setProcessingId(id);
      await sickLeaveRequestApi.approve(id);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Failed to approve.');
    } finally {
      setProcessingId(null);
    }
  };

  const openReject = (req: SickLeaveRequest) => {
    setRejecting(req);
    setRejectReason('');
    setRejectError('');
  };

  const closeReject = () => {
    if (processingId != null) return;
    setRejecting(null);
    setRejectReason('');
    setRejectError('');
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    try {
      setProcessingId(rejecting.sickLeaveRequestId);
      setRejectError('');
      await sickLeaveRequestApi.reject(rejecting.sickLeaveRequestId, {
        rejectionReason: rejectReason.trim() || undefined,
      });
      setRejecting(null);
      setRejectReason('');
      await load();
    } catch (err: any) {
      setRejectError(err?.response?.data?.message || err?.message || 'Failed to reject.');
    } finally {
      setProcessingId(null);
    }
  };

  const downloadCertificate = async (req: SickLeaveRequest) => {
    try {
      const blob = await sickLeaveRequestApi.downloadCertificate(req.sickLeaveRequestId, 'attachment');
      triggerBlobDownload(blob, req.certificateFileName || 'sick-certificate.pdf');
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Could not download certificate.');
    }
  };

  if (!selectedLocationId) {
    return (
      <div className="flex h-64 items-center justify-center text-neutral-500">
        Select a location from the top navigation to manage sick leave.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Sick Leave Admin</h1>
          <p className="text-sm text-neutral-500 mt-1">Review requests and medical certificates.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-300 rounded-md text-sm"
          >
            <option value="">All statuses</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-500 py-12">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center text-neutral-500">
          No sick leave requests found.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Employee</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Dates</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Days</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Certificate</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((req) => (
                <tr key={req.sickLeaveRequestId}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900">
                      {req.employee?.firstName} {req.employee?.lastName}
                    </div>
                    {req.reason && <div className="text-xs text-neutral-500 mt-0.5 max-w-xs truncate">{req.reason}</div>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatCompanyDate(req.startDate)}
                    {req.startDate !== req.endDate && <> – {formatCompanyDate(req.endDate)}</>}
                  </td>
                  <td className="px-4 py-3">{req.totalDays}</td>
                  <td className="px-4 py-3">
                    {!req.certificateRequired ? (
                      <span className="text-neutral-500">Not required</span>
                    ) : req.certificateUploaded ? (
                      <button
                        type="button"
                        onClick={() => downloadCertificate(req)}
                        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <Download className="h-3.5 w-3.5" />
                        {req.certificateFileName || 'Download'}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Missing
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                      req.status === 'Approved' ? 'bg-green-50 text-green-700 border-green-200' :
                      req.status === 'Rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-amber-50 text-amber-800 border-amber-200'
                    }`}>
                      {req.certificateRequired && !req.certificateUploaded && req.status === 'Submitted' && (
                        <FileText className="h-3 w-3" />
                      )}
                      {req.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {req.status === 'Submitted' && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={processingId === req.sickLeaveRequestId || (req.certificateRequired && !req.certificateUploaded)}
                          onClick={() => approve(req.sickLeaveRequestId)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white rounded-md text-xs hover:bg-green-700 disabled:opacity-50"
                          title={req.certificateRequired && !req.certificateUploaded ? 'Certificate required first' : 'Approve'}
                        >
                          <Check className="h-3.5 w-3.5" />
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={processingId === req.sickLeaveRequestId}
                          onClick={() => openReject(req)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 disabled:opacity-50"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-sick-leave-title"
            className="bg-white rounded-lg w-full max-w-md shadow-xl"
          >
            <div className="flex justify-between items-center px-5 py-4 border-b border-neutral-200">
              <h2 id="reject-sick-leave-title" className="text-lg font-semibold text-neutral-900">
                Reject sick leave
              </h2>
              <button
                type="button"
                onClick={closeReject}
                disabled={processingId === rejecting.sickLeaveRequestId}
                className="text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-neutral-600">
                Reject {rejecting.employee?.firstName} {rejecting.employee?.lastName}’s request for{' '}
                {formatCompanyDate(rejecting.startDate)}
                {rejecting.startDate !== rejecting.endDate && <> – {formatCompanyDate(rejecting.endDate)}</>}
                . Timecard absence rows for this request will be removed.
              </p>
              {rejectError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{rejectError}</div>
              )}
              <label className="block text-sm font-medium text-neutral-700">
                Rejection reason (optional)
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm font-normal text-neutral-900"
                  placeholder="Shown to the employee if provided"
                />
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeReject}
                  disabled={processingId === rejecting.sickLeaveRequestId}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void confirmReject()}
                  disabled={processingId === rejecting.sickLeaveRequestId}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {processingId === rejecting.sickLeaveRequestId ? 'Rejecting…' : 'Reject request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SickLeaveAdmin;
